import { forwardRef, useMemo, useState } from "react";
import { sankey as d3Sankey, sankeyLinkHorizontal } from "d3-sankey";
import {
  buildSankeyData,
  NO_RESPONSE_COLOR,
  NO_RESPONSE_NODE,
  STAGE_COLOR,
  STAGE_LABELS,
  Stage,
} from "../utils/applicationHistory";

type AppLike = {
  history?: any;
  predicted_label?: string;
  date?: string;
};

// Shape of the objects d3-sankey produces after layout — only the fields we read.
type LaidOutNode = {
  name: string;
  value: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
};
type LaidOutLink = {
  source: LaidOutNode;
  target: LaidOutNode;
  value: number;
  width: number;
};

export const SANKEY_WIDTH = 720;
export const SANKEY_HEIGHT = 420;
const WIDTH = SANKEY_WIDTH;
const HEIGHT = SANKEY_HEIGHT;
const MARGIN = { top: 16, right: 150, bottom: 16, left: 90 };

// Display node name -> brand colour.
const LABEL_TO_STAGE: Record<string, Stage> = Object.entries(STAGE_LABELS).reduce(
  (acc, [stage, label]) => {
    acc[label] = stage as Stage;
    return acc;
  },
  {} as Record<string, Stage>
);

function nodeColor(name: string): string {
  if (name === NO_RESPONSE_NODE) return NO_RESPONSE_COLOR;
  const stage = LABEL_TO_STAGE[name];
  return stage ? STAGE_COLOR[stage] : "#826751";
}

// The ref points at the chart <svg> so the modal can export it.
const SankeyChart = forwardRef<SVGSVGElement, { apps: AppLike[] }>(function SankeyChart({ apps }, ref) {
  const { nodes, links } = useMemo(() => buildSankeyData(apps), [apps]);
  const [hover, setHover] = useState<
    { x: number; y: number; text: string; index: number } | null
  >(null);

  const total = apps.length;

  const graph = useMemo(() => {
    if (nodes.length < 2 || links.length === 0) return null;
    const layout = (d3Sankey() as any)
      .nodeWidth(14)
      .nodePadding(18)
      .extent([
        [MARGIN.left, MARGIN.top],
        [WIDTH - MARGIN.right, HEIGHT - MARGIN.bottom],
      ]);
    // d3-sankey mutates its input — hand it fresh copies. It throws on bad
    // input; show the empty state then rather than blanking the page.
    try {
      return layout({
        nodes: nodes.map((n) => ({ ...n })),
        links: links.map((l) => ({ ...l })),
      }) as { nodes: LaidOutNode[]; links: LaidOutLink[] };
    } catch (error) {
      console.error("Sankey layout failed:", error);
      return null;
    }
  }, [nodes, links]);

  if (!graph) {
    return (
      <div className="flex h-64 flex-col items-center justify-center text-center text-melt-text2">
        <p className="max-w-md text-sm leading-relaxed">
          Not enough history yet to draw a flow. Once applications move past
          “Applied” — an OA, an interview, a rejection — their paths will show up
          here.
        </p>
      </div>
    );
  }

  const linkPath = sankeyLinkHorizontal() as (l: LaidOutLink) => string | null;

  return (
    <div className="relative w-full">
      <svg
        ref={ref}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full"
        fontFamily="'Space Grotesk', sans-serif"
        role="img"
        aria-label="Sankey diagram of application stage flows"
      >
        {/* links */}
        <g fill="none">
          {graph.links.map((link, i) => {
            const pct = total ? Math.round((link.value / total) * 100) : 0;
            return (
              <path
                key={i}
                d={linkPath(link) || undefined}
                stroke={nodeColor(link.source.name)}
                // Hovered link stands out; the rest step back
                strokeOpacity={hover ? (hover.index === i ? 0.6 : 0.14) : 0.36}
                strokeWidth={Math.max(1, link.width)}
                onMouseEnter={(e) =>
                  setHover({
                    x: e.nativeEvent.offsetX,
                    y: e.nativeEvent.offsetY,
                    index: i,
                    text: `${link.source.name} → ${link.target.name}: ${link.value}${
                      total ? ` (${pct}%)` : ""
                    }`,
                  })
                }
                onMouseMove={(e) =>
                  setHover((h) =>
                    h
                      ? {
                          ...h,
                          x: e.nativeEvent.offsetX,
                          y: e.nativeEvent.offsetY,
                        }
                      : h
                  )
                }
                onMouseLeave={() => setHover(null)}
                style={{ transition: "stroke-opacity 120ms" }}
              />
            );
          })}
        </g>

        {/* nodes */}
        <g>
          {graph.nodes.map((node, i) => {
            const onRight = node.x0 > WIDTH / 2;
            return (
              <g key={i}>
                <rect
                  x={node.x0}
                  y={node.y0}
                  width={Math.max(1, node.x1 - node.x0)}
                  height={Math.max(1, node.y1 - node.y0)}
                  fill={nodeColor(node.name)}
                  rx={3}
                />
                <text
                  x={onRight ? node.x0 - 8 : node.x1 + 8}
                  y={(node.y0 + node.y1) / 2}
                  dy="0.35em"
                  textAnchor={onRight ? "end" : "start"}
                  fill="#5C3417"
                  fontSize={12.5}
                  fontWeight={600}
                >
                  {node.name}
                  <tspan fill="#8A6A52" fontWeight={400} fontFamily="'IBM Plex Mono', monospace" fontSize={11.5}>
                    {"  "}
                    {node.value}
                  </tspan>
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[130%] whitespace-nowrap rounded-lg bg-melt-text1 px-2.5 py-1.5 font-grotesk text-xs text-white shadow-[0_8px_20px_-8px_rgba(92,52,23,.5)]"
          style={{
            left: `${(hover.x / WIDTH) * 100}%`,
            top: `${(hover.y / HEIGHT) * 100}%`,
          }}
        >
          {hover.text}
        </div>
      )}
    </div>
  );
});

export default SankeyChart;
